import React, { useState, useCallback, useEffect } from "react";
import {
  DetalleWrapper,
  CoberturasContainer,
} from "../styles/CierreCuentaClinicaStyles";
import { DetalleContainer } from "../styles/DetalleCierreCuentaClinicaStyles";
import AfiliadoIngresoDetalle from "./AfiliadoIngresoDetalle";
import AutorizacionIngresoDetalle from "./AutorizacionIngresoDetalle";
import CustomSeparator from "../../../components/Common/CustomSeparator";
import CoberturasCierreCuentaTabla from "./CoberturasCierreCuentaTabla";

const DetalleCierreCuentaClinica = ({
  ingreso,
  formularioBloqueadoPorHojaQX = false,
  diasHospitalarios = 3,
  diasRegistroEgresos = 5,
  consultarCoberturasCierreCuentaData,
  consultarCoberturasCierreCuentaLoading,
  authUserData,
  onConsultarCoberturas,
  anioFormulario,
  secuencialFormulario,
  validarServicioAmbulatorioData,
  validarServicioAmbulatorioLoading,
  tipoPssNoMedicoData,
  validarAfiliadoVigente,
  validarAfiliadoVigenteData,
  validarAfiliadoVigenteLoading,
  validarAfiliadoVigenteError,
  limpiarValidarAfiliadoVigente,
  guardarEgreso,
  cambiarServicio,
  cambiarServicioData,
  cambiarServicioLoading,
  cambiarServicioError,
  limpiarCambiarServicio,
  consultarReclamantes,
  consultarReclamantesData,
  consultarReclamantesLoading,
  consultarReclamantesError,
  limpiarConsultarReclamantes,
  tieneDetalle = false,
}) => {
  const [formularioBloqueado, setFormularioBloqueado] = useState(false);
  const [fechaEgresoFormulario, setFechaEgresoFormulario] = useState("");
  const [tipoServicioFormulario, setTipoServicioFormulario] = useState("");
  const [motivoEgresoFormulario, setMotivoEgresoFormulario] = useState("");
  const [coberturasActuales, setCoberturasActuales] = useState([]);
  const [erroresValidacion, setErroresValidacion] = useState({
    tipoServicio: "",
    fechaEgreso: "",
  });

  const handleFormularioBloqueado = useCallback((bloqueado) => {
    setFormularioBloqueado(bloqueado);
  }, []);

  const handleFechaEgresoChange = useCallback((fecha) => {
    setFechaEgresoFormulario(fecha);
    // Limpiar error cuando el usuario ingresa una fecha
    if (fecha) {
      setErroresValidacion((prev) => ({ ...prev, fechaEgreso: "" }));
    }
  }, []);

  const handleTipoServicioChange = useCallback((tipoServicio) => {
    setTipoServicioFormulario(tipoServicio);
    // Limpiar error cuando el usuario selecciona un valor
    if (tipoServicio) {
      setErroresValidacion((prev) => ({ ...prev, tipoServicio: "" }));
    }
  }, []);

  const handleMotivoEgresoChange = useCallback((motivoEgreso) => {
    setMotivoEgresoFormulario(motivoEgreso ?? "");
  }, []);

  const handleCoberturasChange = useCallback((coberturas) => {
    setCoberturasActuales(coberturas);
  }, []);

  // Función para validar campos requeridos antes de enviar reclamación
  const validarCamposRequeridos = useCallback(() => {
    const nuevosErrores = {
      tipoServicio: "",
      fechaEgreso: "",
    };
    let esValido = true;

    if (!tipoServicioFormulario) {
      nuevosErrores.tipoServicio = "Este campo es requerido";
      esValido = false;
    }

    if (!fechaEgresoFormulario) {
      nuevosErrores.fechaEgreso = "Este campo es requerido";
      esValido = false;
    }

    setErroresValidacion(nuevosErrores);
    return esValido;
  }, [tipoServicioFormulario, fechaEgresoFormulario]);

  // Inicializar tipoServicioFormulario y motivoEgresoFormulario con el valor del encabezado
  useEffect(() => {
    if (ingreso) {
      const encabezado = ingreso.ingresoEncabezado || ingreso;
      if (encabezado?.codigoServicio != null && !tipoServicioFormulario) {
        setTipoServicioFormulario(String(encabezado.codigoServicio));
      }
      const motivoFromEncabezado =
        encabezado?.codigoMotivoEgreso != null && encabezado?.codigoMotivoEgreso !== ""
          ? String(encabezado.codigoMotivoEgreso)
          : encabezado?.motivoEgreso || "";
      setMotivoEgresoFormulario(motivoFromEncabezado);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingreso]);

  // Bloquear formulario solo si viene bloqueado por hoja QX desde el padre
  // (el padre consulta el endpoint y determina si debe bloquearse según el estatus)
  useEffect(() => {
    if (formularioBloqueadoPorHojaQX) {
      setFormularioBloqueado(true);
    } else {
      // Si no viene bloqueado por hoja QX, no bloquear por esta razón
      // (puede estar bloqueado por otras razones, pero no por hoja QX)
      // No desbloqueamos aquí para no interferir con otros bloqueos
    }
  }, [formularioBloqueadoPorHojaQX]);

  if (!ingreso) return null;

  const encabezado = ingreso.ingresoEncabezado || ingreso;
  
  // Usar coberturas actuales (en memoria) o del backend
  const coberturas = coberturasActuales.length > 0 ? coberturasActuales : (ingreso?.ingresoDetalle || []);

  return (
    <DetalleWrapper>
      <DetalleContainer>
        <AfiliadoIngresoDetalle encabezado={encabezado} />
      </DetalleContainer>

      <DetalleContainer style={{ marginTop: 12, marginBottom: 20 }}>
        <AutorizacionIngresoDetalle
          encabezado={encabezado}
          ingresoDetalle={coberturas}
          diasHospitalarios={diasHospitalarios}
          diasRegistroEgresos={diasRegistroEgresos}
          validarServicioAmbulatorioData={validarServicioAmbulatorioData}
          validarServicioAmbulatorioLoading={validarServicioAmbulatorioLoading}
          onFormularioBloqueado={handleFormularioBloqueado}
          onFechaEgresoChange={handleFechaEgresoChange}
          onTipoServicioChange={handleTipoServicioChange}
          motivoEgresoControlled={motivoEgresoFormulario}
          onMotivoEgresoChange={handleMotivoEgresoChange}
          validarAfiliadoVigente={validarAfiliadoVigente}
          validarAfiliadoVigenteData={validarAfiliadoVigenteData}
          validarAfiliadoVigenteLoading={validarAfiliadoVigenteLoading}
          validarAfiliadoVigenteError={validarAfiliadoVigenteError}
          limpiarValidarAfiliadoVigente={limpiarValidarAfiliadoVigente}
          guardarEgreso={guardarEgreso}
          cambiarServicio={cambiarServicio}
          cambiarServicioData={cambiarServicioData}
          cambiarServicioLoading={cambiarServicioLoading}
          cambiarServicioError={cambiarServicioError}
          limpiarCambiarServicio={limpiarCambiarServicio}
          authUserData={authUserData}
          erroresValidacion={erroresValidacion}
          formularioBloqueadoPorHojaQX={formularioBloqueadoPorHojaQX}
        />
      </DetalleContainer>

      <CoberturasContainer>
        <CoberturasCierreCuentaTabla
          consultarCoberturasCierreCuentaData={
            consultarCoberturasCierreCuentaData
          }
          consultarCoberturasCierreCuentaLoading={
            consultarCoberturasCierreCuentaLoading
          }
          authUserData={authUserData}
          onConsultarCoberturas={onConsultarCoberturas}
          onCoberturasChange={handleCoberturasChange}
          anioFormulario={anioFormulario}
          secuencialFormulario={secuencialFormulario}
          encabezado={encabezado}
          formularioBloqueado={formularioBloqueado}
          tipoPssNoMedicoData={tipoPssNoMedicoData}
          consultarReclamantes={consultarReclamantes}
          consultarReclamantesData={consultarReclamantesData}
          consultarReclamantesLoading={consultarReclamantesLoading}
          consultarReclamantesError={consultarReclamantesError}
          limpiarConsultarReclamantes={limpiarConsultarReclamantes}
          fechaEgresoFormulario={fechaEgresoFormulario}
          tipoServicioFormulario={tipoServicioFormulario}
          motivoEgresoFormulario={motivoEgresoFormulario}
          validarCamposRequeridos={validarCamposRequeridos}
          tieneDetalle={tieneDetalle}
        />
      </CoberturasContainer>

      <CustomSeparator height={"20px"} />
      <CustomSeparator />
    </DetalleWrapper>
  );
};

export default DetalleCierreCuentaClinica;
